import { Toaster as Sonner, type ToasterProps } from "sonner";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "@/contexts/ThemeContext";
import { useHasExternalToaster } from "./toaster-registry";

/**
 * The application's toaster, replacing react-toastify.
 *
 * Styling comes from the shadcn tokens rather than a stylesheet of overrides, so
 * a toast follows the org's theme for free — including `--radius`, which is why
 * toasts pick up the 6px corner radius without anything here saying 6px.
 *
 * Two behaviours are carried over from the react-toastify container deliberately:
 *
 *  - `duration` 5000 and bottom-right, as before. Bottom-right stays put under
 *    RTL because that is where it has always been; `dir` flips the toast's own
 *    content, which is the part that was actually mirrored before.
 *  - `closeButton`, which used to be a hand-rolled SVG.
 *
 * One behaviour is not carried over: react-toastify drew a progress bar. Sonner
 * has none, and adding one back would mean fighting the component.
 *
 * `richColors` is what makes a success look different from an error. Without it
 * sonner paints every type with the --normal-* colours and distinguishes them by
 * icon alone, which in practice reads as "all the toasts look the same". The
 * per-type colours are defined in index.css, so they come from the org theme and
 * flip with light/dark like everything else.
 */
export const Toaster = (props: ToasterProps) => {
  const { mode } = useThemeMode();
  const { i18n } = useTranslation();
  // omniflow-ui brings its own Toaster and sonner is a shared module, so two
  // would render every toast twice. See toaster-registry.
  const suppressed = useHasExternalToaster();

  if (suppressed) return null;

  return (
    <Sonner
      theme={mode as ToasterProps["theme"]}
      dir={i18n.dir()}
      position="bottom-right"
      duration={5000}
      closeButton
      richColors
      className="toaster group"
      style={
        {
          "--normal-bg": "var(--card)",
          "--normal-text": "var(--card-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
          // Inline, not in a stylesheet: sonner sets these same names on this
          // element, and an element's own custom property beats an inherited
          // one. The values behind them live in index.css, per mode.
          "--success-bg": "var(--toast-success-bg)",
          "--success-text": "var(--toast-success-text)",
          "--success-border": "var(--toast-success-border)",
          "--error-bg": "var(--toast-error-bg)",
          "--error-text": "var(--toast-error-text)",
          "--error-border": "var(--toast-error-border)",
          "--warning-bg": "var(--toast-warning-bg)",
          "--warning-text": "var(--toast-warning-text)",
          "--warning-border": "var(--toast-warning-border)",
        } as React.CSSProperties
      }
      {...props}
    />
  );
};
